import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DeactivatableButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormDateTimeInput,
  FormSelectInput,
  getStopDatePickerProps,
  useAssertedParams,
  StopConfirmation,
  usePolicyStopReasons,
  useStopDateError,
} from '@korint/folio';
import { useMutation } from '@tanstack/react-query';
import { policyControllerStopPolicy } from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { HandIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

type TerminationFormValues = { reason: string; stopDate?: string };

export const TerminationButton = ({ funnelBasePath }: { funnelBasePath: string }) => {
  const { policy } = usePolicyOnDashboard();
  const funnelEnabled = useFeatureFlag('ops_policy_stop_branch', policy.productId);

  return funnelEnabled ? (
    <StopFunnelButton funnelBasePath={funnelBasePath} />
  ) : (
    <TerminationDialogButton />
  );
};

const StopFunnelButton = ({ funnelBasePath }: { funnelBasePath: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policyId } = useAssertedParams();
  const { policy } = usePolicyOnDashboard();

  const { stopReasonConfigs } = usePolicyStopReasons({ productId: policy.productId, policy });
  const stopReasonKeys = stopReasonConfigs.map(({ reason }) => reason);

  return (
    <DeactivatableButton
      size="icon"
      variant="outline"
      tooltipLabel={t('garantiePlusAssistance.terminationButton.tooltip')}
      rules={[{ isValid: stopReasonKeys.length > 0, message: '' }]}
      onClick={() => navigate(`${funnelBasePath}/${policyId}/stop/reason`)}
    >
      <HandIcon className="h-4 w-4" />
    </DeactivatableButton>
  );
};

const TerminationDialogButton = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const { policy, mutate } = usePolicyOnDashboard();

  const { fullRefundReasons, hasNoStopDateReasons, unavailableReasons, stopReasonConfigs } =
    usePolicyStopReasons({ productId: policy.productId, policy });

  const stopReasonKeys = stopReasonConfigs.map(({ reason }) => reason);
  const stopDateError = useStopDateError(policy);

  const schema = useMemo(() => {
    const reasonEnum =
      stopReasonKeys.length > 0
        ? z.enum(stopReasonKeys as [string, ...string[]], {
            required_error: t('garantiePlusAssistance.terminationButton.reasonRequired'),
          })
        : z.string();
    return z
      .object({ reason: reasonEnum, stopDate: z.string().optional() })
      .refine(
        data =>
          fullRefundReasons.has(data.reason) ||
          hasNoStopDateReasons.has(data.reason) ||
          !!data.stopDate,
        {
          path: ['stopDate'],
          message: t('garantiePlusAssistance.terminationButton.stopDateLabel'),
        },
      )
      .superRefine(({ reason, stopDate }, context) => {
        const message = stopDateError({
          reason: stopReasonConfigs.find(({ reason: configReason }) => configReason === reason),
          stopDate,
        });
        if (message) {
          context.addIssue({ code: z.ZodIssueCode.custom, path: ['stopDate'], message });
        }
      });
  }, [
    stopReasonKeys,
    stopReasonConfigs,
    fullRefundReasons,
    hasNoStopDateReasons,
    stopDateError,
    t,
  ]);

  const { policyId } = useAssertedParams();

  const form = useForm<TerminationFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    shouldUnregister: true,
  });

  const { mutateAsync: stopPolicy } = useMutation({
    mutationFn: async (data: TerminationFormValues) => {
      const stopDate =
        fullRefundReasons.has(data.reason) || hasNoStopDateReasons.has(data.reason)
          ? new Date().toISOString()
          : data.stopDate;
      return await policyControllerStopPolicy(policyId, {
        reason: data.reason,
        details: stopDate ? [{ key: 'stopDate', value: stopDate }] : undefined,
      });
    },
    onSuccess: async () => {
      await mutate();
      setOpen(false);
    },
  });

  const selectedReason = form.watch('reason');
  const isFullRefund = fullRefundReasons.has(selectedReason);
  const hasNoStopDate = hasNoStopDateReasons.has(selectedReason);
  const selectedReasonConfig = stopReasonConfigs.find(({ reason }) => reason === selectedReason);

  return (
    <Form {...form}>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          setConfirming(false);
        }}
      >
        <DialogTrigger asChild>
          <DeactivatableButton
            size="icon"
            variant="outline"
            tooltipLabel={t('garantiePlusAssistance.terminationButton.tooltip')}
            rules={[{ isValid: stopReasonKeys.length > 0, message: '' }]}
          >
            <HandIcon className="h-4 w-4" />
          </DeactivatableButton>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('garantiePlusAssistance.terminationButton.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <form
            id="termination-form"
            onSubmit={form.handleSubmit(async values => {
              await stopPolicy(values);
            })}
            className={confirming ? 'hidden' : 'space-y-4'}
          >
            <FormSelectInput
              label={t('garantiePlusAssistance.terminationButton.reasonLabel')}
              placeholder={t('garantiePlusAssistance.terminationButton.reasonPlaceholder')}
              name="reason"
              required
              control={form.control}
              options={stopReasonKeys.map(key => ({
                value: key,
                label: t(`garantiePlusAssistance.stopReasons.${key}`),
                disabled: unavailableReasons.has(key),
              }))}
            />
            {!isFullRefund && !hasNoStopDate && (
              <FormDateTimeInput
                control={form.control}
                name="stopDate"
                label={t('garantiePlusAssistance.terminationButton.stopDateLabel')}
                required
                {...getStopDatePickerProps({ reason: selectedReasonConfig, policy })}
              />
            )}
          </form>
          {confirming ? (
            <StopConfirmation
              formId="termination-form"
              reasonLabel={t(`garantiePlusAssistance.stopReasons.${selectedReason}`)}
              stopDate={isFullRefund || hasNoStopDate ? undefined : form.watch('stopDate')}
              loading={form.formState.isSubmitting}
              onBack={() => setConfirming(false)}
            />
          ) : (
            <DialogFooter>
              <Button type="button" onClick={async () => setConfirming(await form.trigger())}>
                {t('garantiePlusAssistance.terminationButton.submit')}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </Form>
  );
};
